import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useUser } from '../../context/UserContext';
import {
  Palette,
  Search,
  Filter,
  Calendar,
  FileText,
  Star,
  GripVertical,
  FolderOpen,
  Plus,
  Trash2,
  Eye,
  Pencil,
  Image as ImageIcon,
  AlertTriangle,
  X,
} from 'lucide-react';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { EditButton, DeleteButton, ViewButton, SmallIconButton, OutlineButton } from '../../components/Buttons';
import DesignSubmissionModal from '../../components/DesignSubmissionModal';
import DesignViewModal from '../../components/DesignViewModal';
import ConfirmationModal from '../../components/ConfirmationModal';
import PortfolioViewModal from '../../components/PortfolioViewModal';
import LoadingImage from '../../components/LoadingImage';
import { PrimaryButton } from '../../components/Buttons';
import { getPresignedDownloadUrls } from '../../utils/fileUtils';
import axiosInstance from '../../services/api/axios';
import {
  INTERIOR_DESIGNER_DESIGNS_PATH,
  parseDesignsTabFromHash,
  designsHrefWithTab,
} from './designsRoute';
import { toast } from 'react-toastify';
import DataTable from '../../components/DataTable';
import CountdownBadge from '../../components/CountdownBadge';
import { hasStatus, ID_ITER_ACK_EXPIRED } from '../../utils/idLeadStatus';

const FEATURED_DROPPABLE_PREFIX = 'featured-phase-';
const MAX_FEATURED_PER_PHASE = 8;
const FEATURED_PHASE_FILTER_ALL = 'all';

// Derive status string from API format: string, or array like [{ ID_ACKNOWLEDGEMENT_PENDING: "..." }], or object
function getStatusDisplay(raw) {
  if (typeof raw === 'string') return raw;
  if (Array.isArray(raw) && raw.length > 0) {
    const first = raw[0];
    if (first && typeof first === 'object') return Object.keys(first)[0] ?? '-';
  }
  if (raw && typeof raw === 'object') return raw.typeName ?? raw.type ?? Object.keys(raw)[0] ?? '-';
  return '-';
}

const STATUS_LABELS = {
  NEW: 'New',
  CONTACTED: 'Contacted',
  QUOTED: 'Quoted',
  WON: 'Won',
  LOST: 'Lost',
  ID_ACKNOWLEDGEMENT_PENDING: 'Acknowledgement Pending',
  ID_DESIGN_REQUEST_ACKNOWLEDGED: 'Design Request Acknowledged',
  ID_DESIGN_SUBMITTED: 'Design Submitted',
  BUYER_DESIGN_APPROVED: 'Buyer Design Approved',
  BUYER_DESIGN_FEEDBACK_PROVIDED: 'Buyer Feedback Provided',
  BUYER_DESIGN_REJECTED: 'Buyer Design Rejected',
  ID_DESIGN_FEEDBACK_ACKNOWLEDGED: 'Designer Feedback Acknowledged',
  DEVELOPER_PENDING: 'Developer Pending',
  DEVELOPER_APPROVED: 'Developer Approved',
  DEVELOPER_REJECTED: 'Developer Rejected',
};

/**
 * Pick the right status to surface for an iteration row.
 *
 * Why: developer_approval_status defaults to DEVELOPER_PENDING on every
 * iteration regardless of buyer state, so showing it for non-final or
 * non-buyer-approved iterations is misleading. The dev approval gate
 * only meaningfully applies once the buyer has approved a *final*
 * iteration; before that, the iteration's own workflow status is what
 * the designer cares about.
 */
function pickIterationStatus({ iterationDeveloperStatus, iterationWorkflowStatus, rowIterationIsFinal }) {
  const flow = typeof iterationWorkflowStatus === 'string' ? iterationWorkflowStatus.trim() : '';
  const dev = typeof iterationDeveloperStatus === 'string' ? iterationDeveloperStatus.trim() : '';
  const buyerApproved = flow === 'APPROVED';
  // Show dev-approval status only when buyer has approved a final iteration.
  if (rowIterationIsFinal && buyerApproved && dev) return dev;
  return flow || null;
}

function formatStatusLabel(status) {
  if (typeof status !== 'string' || status.trim().length === 0) return '-';
  if (STATUS_LABELS[status]) return STATUS_LABELS[status];
  return status
    .toLowerCase()
    .split('_')
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

function getLatestStatusKey(statusHistory) {
  if (!Array.isArray(statusHistory) || statusHistory.length === 0) return null;
  const latest = statusHistory
    .filter((entry) => entry && typeof entry === 'object' && Object.keys(entry).length > 0)
    .map((entry, idx) => {
      const key = Object.keys(entry)[0];
      const value = entry?.[key];
      const parsed = Date.parse(value);
      return {
        key,
        idx,
        time: Number.isNaN(parsed) ? Number.NEGATIVE_INFINITY : parsed,
      };
    })
    .sort((a, b) => {
      if (a.time === b.time) return b.idx - a.idx;
      return b.time - a.time;
    })[0];
  return latest?.key ?? null;
}

/** Iterations from list payload: submission-level array or lead featured rows */
function getDesignIterations(design) {
  const camel = Array.isArray(design?.designIterations) ? design.designIterations : [];
  if (camel.length > 0) return camel;
  const snake = Array.isArray(design?.design_iterations) ? design.design_iterations : [];
  if (snake.length > 0) return snake;
  const featured = Array.isArray(design?.idLead?.featuredDesignIterations)
    ? design.idLead.featuredDesignIterations
    : [];
  return featured
    .map((row) => row?.designIteration ?? row?.design_iteration)
    .filter(Boolean);
}

/** Unwrap list items shaped like { designSubmission, designIterations } from withIterations responses */
function normalizeSubmissionFromListItem(item) {
  if (!item) return item;
  const sub = item.designSubmission ?? item.design_submission ?? item;
  const iterations =
    item.designIterations ??
    item.design_iterations ??
    sub.designIterations ??
    sub.design_iterations;
  if (!Array.isArray(iterations)) return sub;
  return { ...sub, designIterations: iterations };
}

function getProjectPhaseIdFromLead(idLead) {
  if (!idLead) return null;
  const prop = idLead.property ?? idLead.Property;
  const phase = prop?.projectPhase ?? prop?.project_phase;
  return (
    phase?.projectPhaseId ??
    phase?.project_phase_id ??
    prop?.projectPhaseId ??
    null
  );
}

function extractFeaturedIterationId(item) {
  if (!item) return null;
  return (
    item.designIterationId ??
    item.design_iteration_id ??
    item.designIteration?.designIterationId ??
    item.designIteration?.id ??
    item.design_iteration?.design_iteration_id ??
    null
  );
}

/** Path param for DELETE /interior-designers/featured-iterations/{id} — not the underlying designIterationId. */
function extractFeaturedDesignIterationId(item) {
  if (!item) return null;
  const nested = item.featuredDesignIteration ?? item.featured_design_iteration;
  return (
    item.featuredDesignIterationId ??
    item.featured_design_iteration_id ??
    nested?.featuredDesignIterationId ??
    nested?.featured_design_iteration_id ??
    null
  );
}

function getPhaseSectionTitle(phaseKey, iterationRows) {
  const match = iterationRows.find((row) => String(row.projectPhaseId) === String(phaseKey));
  const name =
    match?.idLead?.property?.projectPhase?.project?.projectName ??
    match?.idLead?.property?.project_phase?.project?.project_name;
  if (name) return `${name} · Phase ${phaseKey}`;
  return `Project phase ${phaseKey}`;
}

/** Row payload for DesignViewModal from a featured API item, merged with grid row when available */
function buildFeaturedViewRow(apiItem, iterationRows) {
  const iterId = extractFeaturedIterationId(apiItem);
  const base = iterationRows.find((r) => Number(r.designIterationId) === Number(iterId));
  const di = apiItem?.designIteration ?? apiItem?.design_iteration;
  const sub = di?.designSubmission ?? di?.design_submission;
  if (base) {
    return {
      ...base,
      title:
        base.gridTitle && base.iterationNumber != null
          ? `${base.gridTitle} · Iter ${base.iterationNumber}`
          : base.gridTitle,
    };
  }
  return {
    _rowKey: `featured-${iterId}`,
    designSubmissionId: sub?.designSubmissionId ?? sub?.id,
    id: sub?.designSubmissionId ?? sub?.id,
    gridTitle: sub?.title ?? di?.title ?? 'Featured design',
    gridSubtitle: di?.iterationNumber != null ? `Iteration ${di.iterationNumber}` : 'Featured',
    designIterationId: iterId,
    iterationNumber: di?.iterationNumber ?? di?.iteration_number,
    images: Array.isArray(di?.media) ? di.media : [],
    idLead: null,
    title: sub?.title ?? di?.title ?? 'Featured design',
  };
}

/* Feature-from-All-iterations grid: paused — feature from lead request details instead. Re-enable with modal + state below.
function FeaturedBrowseConfirmDetails({ row }) {
  const lead = row?.idLead ?? row?.id_lead;
  const prop = lead?.property ?? lead?.Property;
  const projectName = prop?.projectPhase?.project?.projectName ?? prop?.project_phase?.project?.project_name ?? '—';
  const propertyNo = prop?.propertyNo ?? prop?.property_no ?? '—';
  const configName = lead?.propertyConfig?.configName ?? lead?.property_config?.config_name ?? '—';

  const rows = [
    { label: 'Design', value: row?.gridTitle || row?.title || '—' },

    { label: 'Project', value: projectName },
    { label: 'Property', value: propertyNo },
    { label: 'Configuration', value: configName },
 
  ];

  return (
    <div className="space-y-4">
      <p className="text-sm text-gray-600 leading-relaxed">
        This iteration will be highlighted when buyers browse designs for this project phase. You can feature a different iteration later if needed.
      </p>
      <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 text-sm space-y-2">
        {rows.map(({ label, value }) => (
          <div key={label} className="flex gap-2 min-w-0">
            <span className="text-gray-500 shrink-0 w-36">{label}</span>
            <span className="font-medium text-gray-900 break-words min-w-0">{value}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
*/

/** Whether the latest iteration for this submission is marked final; false if none */
function isLatestIterationFinal(design) {
  const iterations = getDesignIterations(design);
  if (iterations.length === 0) return false;
  const sorted = [...iterations].sort((a, b) => {
    const na = Number(a.iterationNumber ?? a.iteration_number ?? 0);
    const nb = Number(b.iterationNumber ?? b.iteration_number ?? 0);
    if (nb !== na) return nb - na;
    const ida = Number(a.designIterationId ?? a.id ?? 0);
    const idb = Number(b.designIterationId ?? b.id ?? 0);
    return idb - ida;
  });
  const latest = sorted[0];
  return Boolean(latest?.isFinal ?? latest?.is_final);
}

/** One grid row per design iteration (submissions with no iterations become a single placeholder row). */
function flattenSubmissionsToIterationRows(submissions) {
  const rows = [];
  for (const sub of submissions) {
    const subId = sub.designSubmissionId ?? sub.id;
    const iterations = getDesignIterations(sub);
    const projectPhaseId = getProjectPhaseIdFromLead(sub.idLead ?? sub.id_lead);
    if (iterations.length === 0) {
      rows.push({
        ...sub,
        _rowKey: `sub-${subId}`,
        _iteration: null,
        designIterationId: null,
        iterationNumber: null,
        rowIterationIsFinal: false,
        projectPhaseId,
        displayCreatedDate: sub.createdDate,
        gridTitle: sub.title || 'Untitled Design',
        gridSubtitle: 'No iteration yet',
        iterationWorkflowStatus: null,
        iterationDeveloperStatus: null,
        rowDesignerNotes: '',
        designerNotes: typeof sub.designerNotes === 'string' ? sub.designerNotes : '',
      });
      continue;
    }
    const sortedNewestFirst = [...iterations].sort((a, b) => {
      const na = Number(a.iterationNumber ?? a.iteration_number ?? 0);
      const nb = Number(b.iterationNumber ?? b.iteration_number ?? 0);
      if (nb !== na) return nb - na;
      const ida = Number(a.designIterationId ?? a.id ?? 0);
      const idb = Number(b.designIterationId ?? b.id ?? 0);
      return idb - ida;
    });
    for (const iter of sortedNewestFirst) {
      const iterId = iter.designIterationId ?? iter.id;
      const num = iter.iterationNumber ?? iter.iteration_number ?? '?';
      const iterationDisplayName =
        iter.interactionName ??
        iter.interaction_name ??
        iter.iterationName ??
        iter.iteration_name ??
        iter.title ??
        null;
      const media = iter.media;
      const iterImages = Array.isArray(media)
        ? media
        : media && typeof media === 'object'
          ? [media]
          : [];
      const images = iterImages.length > 0 ? iterImages : sub.images;
      const ep = iter.estimatedPrice ?? iter.estimated_price ?? sub.estimatedPrice;
      const sortDate =
        iter.feedbackDate ||
        iter.developerApprovalDate ||
        iter.createdDate ||
        sub.createdDate;
      rows.push({
        ...sub,
        _rowKey: `iter-${subId}-${iterId}`,
        _iteration: iter,
        designIterationId: iterId,
        iterationNumber: num,
        rowIterationIsFinal: Boolean(iter.isFinal ?? iter.is_final),
        projectPhaseId,
        images,
        estimatedPrice: ep,
        displayCreatedDate: sortDate,
        gridTitle: sub.title || 'Untitled Design',
        gridSubtitle:
          typeof iterationDisplayName === 'string' && iterationDisplayName.trim().length > 0
            ? iterationDisplayName.trim()
            : `Iteration ${num}`,
        iterationWorkflowStatus: iter.iterationStatus ?? iter.iteration_status ?? null,
        iterationDeveloperStatus: iter.developerApprovalStatus ?? iter.developer_approval_status ?? null,
        rowDesignerNotes: (iter.designerNotes ?? iter.designer_notes ?? '').trim(),
        designerNotes: (iter.designerNotes ?? iter.designer_notes ?? sub.designerNotes ?? '').trim(),
        // Deadlines / ack tracking for countdown UI (per-iteration).
        iterIdAcknowledgementDeadline:
          iter.idAcknowledgementDeadline ?? iter.id_acknowledgement_deadline ?? null,
        iterIsIdAcknowledged: Boolean(iter.isIdAcknowledged ?? iter.is_id_acknowledged),
      });
    }
  }
  rows.sort((a, b) => {
    const ta = Date.parse(a.displayCreatedDate) || 0;
    const tb = Date.parse(b.displayCreatedDate) || 0;
    return tb - ta;
  });
  return rows;
}

function collectDesignImages(design, portfolioItem, idLead) {
  const directImages = Array.isArray(design?.images) ? design.images : [];
  if (directImages.length > 0) return directImages;

  const portfolioImages = Array.isArray(portfolioItem?.images) ? portfolioItem.images : [];
  if (portfolioImages.length > 0) return portfolioImages;

  const featuredIterations = Array.isArray(idLead?.featuredDesignIterations)
    ? idLead.featuredDesignIterations
    : [];
  const featuredMedia = featuredIterations.flatMap((featured) => {
    const media = featured?.designIteration?.media;
    if (Array.isArray(media)) return media;
    if (media && typeof media === 'object') return [media];
    return [];
  });
  if (featuredMedia.length > 0) return featuredMedia;

  const leadMedia = idLead?.media ?? design?.media;
  if (typeof leadMedia === 'string' && leadMedia.trim().length > 0) {
    return [leadMedia];
  }

  return [];
}

// Normalize a design from API for modals and grid (id, images, estimatedPrice, portfolioId, propertyConfigId, submissionStatus)
function normalizeDesign(design) {
  if (!design) return design;
  const idLead = design.idLead;
  const portfolioItem = idLead?.interiorDesignerPortfolioItems?.[0];
  const latestLeadStatus = getLatestStatusKey(idLead?.status);
  const rawSubmissionStatus = design.submissionStatus ?? getStatusDisplay(design.status);
  return {
    ...design,
    id: design.id ?? design.designSubmissionId,
    designSubmissionId: design.designSubmissionId ?? design.id,
    title: design.title ?? portfolioItem?.title ?? '',
    description: design.description ?? portfolioItem?.description ?? '',
    images: collectDesignImages(design, portfolioItem, idLead),
    estimatedPrice: design.estimatedPrice ?? portfolioItem?.overallCost ?? null,
    portfolioId: design.portfolioId ?? portfolioItem?.interiorDesignerPortfolioItemId ?? design.interiorDesignerPortfolioItemId ?? design.portfolio_id,
    propertyConfigId: design.propertyConfigId ?? idLead?.propertyConfig?.propertyConfigId ?? design.configId,
    submissionStatus: latestLeadStatus ?? rawSubmissionStatus,
    rawSubmissionStatus,
    latestIterationIsFinal: isLatestIterationFinal(design),
  };
}

const Designs = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { userInfo } = useUser();
  const [designs, setDesigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  /** ALL | FINAL | SAMPLES — SAMPLES = latest iteration not final (incl. no iterations yet) */
  const [finalFilter, setFinalFilter] = useState('ALL');
  const [editingDesign, setEditingDesign] = useState(null);
  const [viewingDesign, setViewingDesign] = useState(null);
  const [showDesignModal, setShowDesignModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deletingDesignId, setDeletingDesignId] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [designModalStep, setDesignModalStep] = useState(0);
  const gridRef = useRef(null);
  /** Map projectPhaseId (string) -> featured API rows in browse order */
  const [featuredByPhase, setFeaturedByPhase] = useState({});
  const [featuredRefreshNonce, setFeaturedRefreshNonce] = useState(0);
  /** phaseKey -> user changed order since last save */
  const [dirtyOrderPhases, setDirtyOrderPhases] = useState({});
  const [savingOrderPhaseKey, setSavingOrderPhaseKey] = useState(null);
  /** Filter pill state for the Featured tab. 'all' or a phaseKey string. */
  const [featuredPhaseFilter, setFeaturedPhaseFilter] = useState(FEATURED_PHASE_FILTER_ALL);
  /** When a slot is clicked, open the picker for that phaseKey. */
  const [featuredPickerPhaseKey, setFeaturedPickerPhaseKey] = useState(null);
  const [mainTab, setMainTab] = useState(() =>
    typeof window !== 'undefined' ? parseDesignsTabFromHash(window.location.hash) : 'iterations'
  );
  /* Paused: feature-from-grid (use lead request details).
  const [featuringRowKey, setFeaturingRowKey] = useState(null);
  const [featuredConfirmRow, setFeaturedConfirmRow] = useState(null);
  */

  const [portfolioItems, setPortfolioItems] = useState([]);
  const [loadingPortfolio, setLoadingPortfolio] = useState(false);
  const [selectedPortfolio, setSelectedPortfolio] = useState(null);
  const [portfolioImageUrls, setPortfolioImageUrls] = useState({});
  const [portfolioDeleteTarget, setPortfolioDeleteTarget] = useState(null);
  const [isDeletingPortfolio, setIsDeletingPortfolio] = useState(false);
  /** Featured tab: confirm removal from browse ({ apiItem, title, phaseKey }). */
  const [removeFeaturedTarget, setRemoveFeaturedTarget] = useState(null);
  const [isRemovingFeatured, setIsRemovingFeatured] = useState(false);

  const fetchPortfolio = useCallback(async () => {
    try {
      setLoadingPortfolio(true);
      const response = await axiosInstance.get('/interior-designers/portfolio');
      setPortfolioItems(Array.isArray(response) ? response : response?.data ?? []);
    } catch (error) {
      console.error('Error fetching portfolio:', error);
      if (error.response?.status !== 404) {
        toast.error('Failed to load portfolio');
      }
      setPortfolioItems([]);
    } finally {
      setLoadingPortfolio(false);
    }
  }, []);

  useEffect(() => {
    if (!userInfo?.loginId) return undefined;
    fetchPortfolio();
    return undefined;
  }, [userInfo?.loginId, fetchPortfolio]);

  /** Keep active tab in sync with URL hash (#iterations | #featured | #portfolio). */
  useEffect(() => {
    if (location.pathname !== INTERIOR_DESIGNER_DESIGNS_PATH) return undefined;
    const tab = parseDesignsTabFromHash(location.hash);
    const canonical = `#${tab}`;
    const h = location.hash || '';
    if (!h || h === '#') {
      navigate(designsHrefWithTab('iterations'), { replace: true });
      return undefined;
    }
    if (h !== canonical) {
      navigate(designsHrefWithTab(tab), { replace: true });
      return undefined;
    }
    setMainTab(tab);
    return undefined;
  }, [location.pathname, location.hash, navigate]);

  const selectMainTab = useCallback(
    (tab) => {
      setMainTab(tab);
      navigate(designsHrefWithTab(tab), { replace: true });
    },
    [navigate]
  );

  useEffect(() => {
    const fetchUrls = async () => {
      const allImages = portfolioItems.flatMap((item) => item.images || []);
      const s3Keys = allImages.map((img) => img?.s3_key || img?.s3Key).filter(Boolean);
      if (s3Keys.length === 0) {
        setPortfolioImageUrls({});
        return;
      }
      try {
        const res = await getPresignedDownloadUrls(s3Keys, axiosInstance);
        const map = {};
        res.forEach((item, index) => {
          map[s3Keys[index]] = item.download_url || item.downloadUrl || item.url;
        });
        setPortfolioImageUrls(map);
      } catch (err) {
        console.error(err);
      }
    };
    fetchUrls();
  }, [portfolioItems]);

  const handlePortfolioDeleteConfirm = async () => {
    if (!portfolioDeleteTarget?.id) return;
    try {
      setIsDeletingPortfolio(true);
      await axiosInstance.delete(`/interior-designers/portfolio/${portfolioDeleteTarget.id}`);
      toast.success('Portfolio deleted successfully');
      setPortfolioDeleteTarget(null);
      await fetchPortfolio();
      try {
        window.dispatchEvent(new Event('idPortfolioUpdated'));
      } catch (e) { /* no-op */ }
    } catch (error) {
      console.error('Error deleting portfolio:', error);
      toast.error(error.response?.data?.message || 'Failed to delete portfolio');
    } finally {
      setIsDeletingPortfolio(false);
    }
  };

  const handleRemoveFeaturedConfirm = async () => {
    const target = removeFeaturedTarget;
    if (!target?.apiItem) return;
    const featuredId = extractFeaturedDesignIterationId(target.apiItem);
    if (featuredId == null) {
      toast.error('Could not resolve featured iteration id.');
      setRemoveFeaturedTarget(null);
      return;
    }
    try {
      setIsRemovingFeatured(true);
      await axiosInstance.delete(`/interior-designers/featured-iterations/${Number(featuredId)}`);
      toast.success('Removed from featured browse.');
      setRemoveFeaturedTarget(null);
      setDirtyOrderPhases((d) =>
        target.phaseKey != null ? { ...d, [String(target.phaseKey)]: false } : d
      );
      setFeaturedRefreshNonce((n) => n + 1);
    } catch (error) {
      console.error('Error removing featured iteration:', error);
      const d = error.response?.data?.detail;
      const detailMsg = Array.isArray(d)
        ? d.map((x) => x?.msg || x?.message).filter(Boolean).join(' ')
        : typeof d === 'string'
          ? d
          : null;
      toast.error(detailMsg || error.response?.data?.message || 'Failed to remove from featured.');
    } finally {
      setIsRemovingFeatured(false);
    }
  };

  const fetchDesigns = useCallback(async () => {
    try {
      if (!userInfo?.loginId) {
        setLoading(false);
        return;
      }
      setLoading(true);
      const interiorDesignerId =
        userInfo?.interiorDesignerId ??
        userInfo?.loginIdentifierDetails?.interiorDesigner?.interiorDesignerId ??
        userInfo.loginId;

      const response = await axiosInstance.get('/interior-designers/design-deliverables', {
        params: { interiorDesignerId },
      });
      const deliverables = Array.isArray(response)
        ? response
        : Array.isArray(response?.items)
        ? response.items
        : Array.isArray(response?.data)
        ? response.data
        : [];
      // Group deliverables into legacy pseudo-submission wrappers (one per lead) so the
      // existing normalizeSubmissionFromListItem mapping keeps working.
      const byLead = {};
      for (const d of deliverables) {
        const lid = d.idLeadId;
        if (lid == null) continue;
        if (!byLead[lid]) {
          byLead[lid] = { designSubmission: { designSubmissionId: lid }, designIterations: [] };
        }
        byLead[lid].designIterations.push({ ...d, iterationStatus: d.status });
      }
      const designsArray = Object.values(byLead);
      const mappedDesigns = designsArray.map((item) => {
        const raw = normalizeSubmissionFromListItem(item);
        return normalizeDesign({ ...raw, images: raw?.images ?? [] });
      });
      setDesigns(mappedDesigns);
    } catch (error) {
      console.error('Error fetching designs:', error);
      toast.error('Failed to fetch designs');
    } finally {
      setLoading(false);
    }
  }, [
    userInfo?.loginId,
    userInfo?.interiorDesignerId,
    userInfo?.loginIdentifierDetails?.interiorDesigner?.interiorDesignerId,
  ]);

  useEffect(() => {
    if (userInfo?.loginId) fetchDesigns();
  }, [fetchDesigns, userInfo?.loginId]);

  const uniqueProjectPhaseIds = useMemo(() => {
    const s = new Set();
    for (const d of designs) {
      const pid = getProjectPhaseIdFromLead(d.idLead ?? d.id_lead);
      if (pid != null && pid !== '') s.add(String(pid));
    }
    return Array.from(s).sort();
  }, [designs]);

  const featuredFetchPhaseKey = useMemo(() => uniqueProjectPhaseIds.join(','), [uniqueProjectPhaseIds]);

  useEffect(() => {
    const phaseKeys = featuredFetchPhaseKey ? featuredFetchPhaseKey.split(',').filter(Boolean) : [];
    if (!userInfo?.loginId || phaseKeys.length === 0) {
      setFeaturedByPhase({});
      return undefined;
    }
    let cancelled = false;
    (async () => {
      const next = {};
      await Promise.all(
        phaseKeys.map(async (phaseKey) => {
          const projectPhaseId = Number(phaseKey);
          try {
            const res = await axiosInstance.get('/interior-designers/featured-iterations', {
              params: { projectPhaseId },
            });
            const body = res?.data ?? res;
            const items = Array.isArray(body?.items)
              ? body.items
              : Array.isArray(body)
                ? body
                : [];
            next[phaseKey] = items;
          } catch (e) {
            console.error('Error fetching featured iterations:', projectPhaseId, e);
            next[phaseKey] = [];
          }
        })
      );
      if (!cancelled) setFeaturedByPhase(next);
    })();
    return () => {
      cancelled = true;
    };
  }, [featuredFetchPhaseKey, featuredRefreshNonce, userInfo?.loginId]);

  const getStatusColor = useCallback((status) => {
    const s = typeof status === 'string' ? status : '';
    switch (s) {
      case 'WON':
      case 'BUYER_DESIGN_APPROVED':
      case 'DEVELOPER_APPROVED':
        return 'bg-green-100 text-green-800';
      case 'QUOTED':
      case 'ID_DESIGN_SUBMITTED':
      case 'SUBMITTED':
        return 'bg-blue-100 text-blue-800';
      case 'CONTACTED':
      case 'BUYER_DESIGN_FEEDBACK_PROVIDED':
      case 'ID_DESIGN_FEEDBACK_ACKNOWLEDGED':
        return 'bg-yellow-100 text-yellow-800';
      case 'LOST':
      case 'BUYER_DESIGN_REJECTED':
      case 'DEVELOPER_REJECTED':
        return 'bg-red-100 text-red-800';
      case 'ID_ACKNOWLEDGEMENT_PENDING':
      case 'ID_DESIGN_REQUEST_ACKNOWLEDGED':
      case 'DEVELOPER_PENDING':
        return 'bg-amber-100 text-amber-800';
      case 'NEW':
      case 'DRAFT':
      default:
        return 'bg-gray-100 text-gray-700';
    }
  }, []);

  const formatDate = useCallback((dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    } catch {
      return dateString;
    }
  }, []);

  const iterationRows = useMemo(() => flattenSubmissionsToIterationRows(designs), [designs]);

  const featuredIdsByPhase = useMemo(() => {
    const out = {};
    for (const [phaseKey, arr] of Object.entries(featuredByPhase)) {
      if (!Array.isArray(arr)) continue;
      out[phaseKey] = arr
        .map((item) => extractFeaturedIterationId(item))
        .filter((id) => id != null)
        .map(Number);
    }
    return out;
  }, [featuredByPhase]);

  const totalFeaturedCount = useMemo(
    () =>
      Object.values(featuredByPhase).reduce((acc, arr) => acc + (Array.isArray(arr) ? arr.length : 0), 0),
    [featuredByPhase]
  );

  /**
   * Metadata for each phase the designer has leads in, in sorted order.
   * Each entry: { phaseKey, projectName, phaseName, label, featuredCount }.
   * Drives the pill-bar at the top of the Featured tab.
   */
  const phasesMeta = useMemo(() => {
    return uniqueProjectPhaseIds.map((phaseKey) => {
      const match = iterationRows.find(
        (row) => String(row.projectPhaseId) === String(phaseKey)
      );
      const phase =
        match?.idLead?.property?.projectPhase ??
        match?.idLead?.property?.project_phase ??
        null;
      const projectName =
        phase?.project?.projectName ?? phase?.project?.project_name ?? null;
      const phaseName = phase?.name ?? phase?.phase_name ?? null;
      const label = projectName
        ? phaseName
          ? `${projectName} · ${phaseName}`
          : `${projectName} · Phase ${phaseKey}`
        : `Phase ${phaseKey}`;
      const featuredCount = Array.isArray(featuredByPhase[phaseKey])
        ? featuredByPhase[phaseKey].length
        : 0;
      return { phaseKey, projectName, phaseName, label, featuredCount };
    });
  }, [uniqueProjectPhaseIds, iterationRows, featuredByPhase]);

  /** Iterations the designer owns for the given phase that aren't already featured. */
  const getCandidateIterationsForPhase = useCallback(
    (phaseKey) => {
      if (!phaseKey) return [];
      const featuredIds = new Set(
        (featuredIdsByPhase[phaseKey] ?? []).map(Number)
      );
      return iterationRows.filter((row) => {
        if (String(row.projectPhaseId) !== String(phaseKey)) return false;
        const id = Number(row.designIterationId ?? row.id);
        if (!Number.isFinite(id)) return false;
        return !featuredIds.has(id);
      });
    },
    [iterationRows, featuredIdsByPhase]
  );

  const [featuringIterationId, setFeaturingIterationId] = useState(null);
  const handleFeatureIterationInPhase = useCallback(
    async (phaseKey, iterationId) => {
      if (!phaseKey || !iterationId) return;
      setFeaturingIterationId(iterationId);
      try {
        await axiosInstance.post('/interior-designers/featured-iterations', {
          projectPhaseId: Number(phaseKey),
          designIterationIds: [Number(iterationId)],
          propertyConfigCosts: [],
        });
        toast.success('Added to featured browse.');
        setFeaturedRefreshNonce((n) => n + 1);
        setFeaturedPickerPhaseKey(null);
      } catch (error) {
        const detail = error?.response?.data?.detail || error?.response?.data?.message;
        toast.error(detail || 'Failed to feature iteration.');
      } finally {
        setFeaturingIterationId(null);
      }
    },
    []
  );

  const getPortfolioPopularityCount = useCallback(
    (item) => Number(item?.buyerShortlistsCount ?? item?.buyer_shortlists_count ?? 0) || 0,
    []
  );

  const sortedPortfolioItems = useMemo(() => {
    return [...portfolioItems].sort((a, b) => {
      const countDiff = getPortfolioPopularityCount(b) - getPortfolioPopularityCount(a);
      if (countDiff !== 0) return countDiff;
      return String(a?.title ?? '').localeCompare(String(b?.title ?? ''));
    });
  }, [portfolioItems, getPortfolioPopularityCount]);

  const handleFeaturedDragEnd = useCallback((result) => {
    const { destination, source } = result;
    if (!destination) return;
    if (source.droppableId !== destination.droppableId) return;
    if (!source.droppableId.startsWith(FEATURED_DROPPABLE_PREFIX)) return;
    const phaseKey = source.droppableId.slice(FEATURED_DROPPABLE_PREFIX.length);
    if (source.index === destination.index) return;
    setFeaturedByPhase((prev) => {
      const list = [...(prev[phaseKey] || [])];
      const [removed] = list.splice(source.index, 1);
      list.splice(destination.index, 0, removed);
      return { ...prev, [phaseKey]: list };
    });
    setDirtyOrderPhases((d) => ({ ...d, [phaseKey]: true }));
  }, []);

  const saveFeaturedOrder = useCallback(
    async (phaseKey) => {
      const items = featuredByPhase[phaseKey] || [];
      const ids = items.map((item) => extractFeaturedIterationId(item)).filter((id) => id != null).map(Number);
      if (ids.length === 0) return;
      setSavingOrderPhaseKey(phaseKey);
      try {
        await axiosInstance.post('/interior-designers/featured-iterations', {
          projectPhaseId: Number(phaseKey),
          designIterationIds: ids,
          propertyConfigCosts: [],
        });
        toast.success('Browse order saved.');
        setDirtyOrderPhases((d) => ({ ...d, [phaseKey]: false }));
        setFeaturedRefreshNonce((n) => n + 1);
      } catch (e) {
        console.error('Error saving featured order:', e);
        toast.error(e.response?.data?.message || 'Failed to save order.');
      } finally {
        setSavingOrderPhaseKey(null);
      }
    },
    [featuredByPhase]
  );

  const filteredRows = useMemo(() => {
    const q = searchQuery.toLowerCase();
    return iterationRows
      .map((row) => {
        const phaseKey = row.projectPhaseId != null && row.projectPhaseId !== '' ? String(row.projectPhaseId) : null;
        const list = phaseKey ? featuredIdsByPhase[phaseKey] ?? [] : [];
        const isFeaturedForBrowse =
          row.designIterationId != null &&
          phaseKey != null &&
          list.map(Number).includes(Number(row.designIterationId));
        return { ...row, isFeaturedForBrowse };
      })
      .filter((row) => {
        const matchesSearch =
          row.gridTitle?.toLowerCase().includes(q) ||
          row.description?.toLowerCase().includes(q) ||
          String(row.iterationNumber ?? '').includes(q) ||
          row.rowDesignerNotes?.toLowerCase().includes(q);
        const isFinal = row.rowIterationIsFinal;
        const matchesFinal =
          finalFilter === 'ALL' ||
          (finalFilter === 'FINAL' && isFinal) ||
          (finalFilter === 'SAMPLES' && !isFinal);
        return matchesSearch && matchesFinal;
      });
  }, [iterationRows, searchQuery, finalFilter, featuredIdsByPhase]);

  const handleEditClick = useCallback((design) => {
    setEditingDesign(design);
    setDesignModalStep(0);
    setShowDesignModal(true);
  }, []);

  const handleEditBoqClick = useCallback((design) => {
    setEditingDesign(design);
    setDesignModalStep(1);
    setShowDesignModal(true);
  }, []);

  const handleViewClick = useCallback((row) => {
    const viewPayload = {
      ...row,
      title: row.designIterationId
        ? `${row.gridTitle} · Iter ${row.iterationNumber}`
        : row.gridTitle,
    };
    setViewingDesign(viewPayload);
  }, []);

  const handleDeleteClick = useCallback((designId) => {
    setDeletingDesignId(designId);
    setShowDeleteConfirm(true);
  }, []);

  const handleDeleteConfirm = useCallback(async () => {
    if (!deletingDesignId || !userInfo?.loginId) return;
    // Design deliverables are append-only in the arch-parity model — there is no delete
    // endpoint. Surface that instead of hitting a removed route.
    toast.info('Submitted designs are part of the design history and cannot be deleted.');
    setShowDeleteConfirm(false);
    setDeletingDesignId(null);
    return;
    // eslint-disable-next-line no-unreachable
    setIsDeleting(true);
    try {
      await axiosInstance.delete(`/interior-designers/${userInfo.loginId}/design-submissions/${deletingDesignId}`);
      toast.success('Design deleted successfully!');
      setShowDeleteConfirm(false);
      setDeletingDesignId(null);
      fetchDesigns();
    } catch (error) {
      console.error('Error deleting design:', error);
      toast.error(error.response?.data?.message || 'Failed to delete design. Please try again.');
    } finally {
      setIsDeleting(false);
    }
  }, [deletingDesignId, userInfo?.loginId, fetchDesigns]);

  /* Paused: openFeaturedConfirm / handleFeaturedConfirmClose / handleFeaturedConfirmSubmit — feature from lead request details.

  const openFeaturedConfirm = useCallback((row) => {
    setFeaturedConfirmRow(row);
  }, []);

  const handleFeaturedConfirmClose = useCallback(() => {
    if (!featuringRowKey) setFeaturedConfirmRow(null);
  }, [featuringRowKey]);

  const handleFeaturedConfirmSubmit = useCallback(async () => {
    const row = featuredConfirmRow;
    if (!row) return;
    const projectPhaseId = row.projectPhaseId;
    const designIterationId = row.designIterationId;
    if (projectPhaseId == null || projectPhaseId === '' || designIterationId == null) {
      toast.warn('Cannot feature: missing project phase or iteration.');
      setFeaturedConfirmRow(null);
      return;
    }
    setFeaturingRowKey(row._rowKey);
    try {
      await axiosInstance.post('/interior-designers/featured-iterations', {
        projectPhaseId,
        designIterationIds: [Number(designIterationId)],
        propertyConfigCosts: [],
      });
      toast.success('Iteration is now featured for browse.');
      setFeaturedRefreshNonce((n) => n + 1);
      setFeaturedConfirmRow(null);
    } catch (error) {
      console.error('Error featuring iteration:', error);
      toast.error(error.response?.data?.message || 'Failed to set as featured.');
    } finally {
      setFeaturingRowKey(null);
    }
  }, [featuredConfirmRow]);
  */

  const handleModalClose = useCallback(() => {
    setShowDesignModal(false);
    setEditingDesign(null);
  }, []);

  const columnDefs = useMemo(
    () => [
      {
        headerName: 'Design',
        width: 170,
        pinned: 'left',
        cellRenderer: (params) => {
          const row = params.data;
          return (
            <div className="flex items-center gap-3 h-full py-1">
              <div className="min-w-0 flex-1">
                <div className="font-semibold text-gray-900 text-sm truncate">{row.gridTitle}</div>
                <div className="text-xs text-gray-500 truncate">{row.gridSubtitle}</div>
              </div>
            </div>
          );
        },
        filter: false,
      },
      {
        headerName: 'Unit No.',
        width: 100,
        valueGetter: (params) => params.data?.idLead?.property?.propertyNo ?? '-',
        filter: false,
      },
      {
        headerName: 'Configuration',
        width: 110,
        valueGetter: (params) => params.data?.idLead?.propertyConfig?.configName ?? '-',
        filter: false,
      },
      {
        headerName: 'Project',
        width: 160,
        flex: 1,
        valueGetter: (params) =>
          params.data?.idLead?.property?.projectPhase?.project?.projectName ?? '-',
        filter: false,
      },
      {
        headerName: 'Type',
        width: 92,
        cellRenderer: (params) => {
          const hasIter = params.data?.designIterationId != null;
          const isFinal = params.data?.rowIterationIsFinal;
          const label = !hasIter ? '—' : isFinal ? 'Final' : 'Sample';
          const colorClass = !hasIter
            ? 'bg-gray-100 text-gray-500'
            : isFinal
              ? 'bg-emerald-100 text-emerald-800'
              : 'bg-slate-100 text-slate-700';
          return (
            <div className="flex items-center h-full">
              <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${colorClass}`}>
                {label}
              </span>
            </div>
          );
        },
        filter: false,
        sortable: false,
      },
      {
        headerName: 'Status',
        width: 160,
        cellRenderer: (params) => {
          let statusStr = pickIterationStatus({
            iterationDeveloperStatus: params.data?.iterationDeveloperStatus,
            iterationWorkflowStatus: params.data?.iterationWorkflowStatus,
            rowIterationIsFinal: params.data?.rowIterationIsFinal,
          });
          if (!statusStr) {
            statusStr =
              getLatestStatusKey(params.data?.idLead?.status) ??
              params.data?.submissionStatus ??
              getStatusDisplay(params.data?.status);
          }
          const displayStatus = formatStatusLabel(statusStr);
          const colorClass = statusStr && statusStr !== '-'
            ? getStatusColor(statusStr)
            : 'bg-gray-100 text-gray-500';
          return (
            <div className="flex items-center h-full">
              <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${colorClass}`}>
                {displayStatus}
              </span>
            </div>
          );
        },
        filter: false,
      },
      {
        headerName: 'Price',
        width: 120,
        cellRenderer: (params) => {
          const price = params.data?.estimatedPrice ?? params.data?.idLead?.interiorDesignerPortfolioItems?.[0]?.overallCost;
          const val = price != null ? parseFloat(price) : null;
          return (
            <div className="flex items-center h-full">
              <span className="text-sm font-semibold text-gray-900">
                {val != null ? `₹${Number(val).toLocaleString()}` : 'N/A'}
              </span>
            </div>
          );
        },
        filter: false,
      },
      {
        headerName: 'Feature Status',
        width: 130,
        cellRenderer: (params) => {
          const row = params.data;
          const hasIter = row?.designIterationId != null;
          const hasPhase = row?.projectPhaseId != null && row.projectPhaseId !== '';
          const featured = row?.isFeaturedForBrowse;
          if (!hasIter) {
            return (
              <div className="flex items-center h-full">
                <span className="text-xs text-gray-400">—</span>
              </div>
            );
          }
          if (!hasPhase) {
            return (
              <div className="flex items-center h-full" title="Lead has no project phase">
                <span className="text-xs text-amber-700">No phase</span>
              </div>
            );
          }
          if (featured) {
            return (
              <div className="flex items-center h-full">
                <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-900">
                  <Star size={12} className="shrink-0 fill-amber-500 text-amber-600" aria-hidden />
                  Featured
                </span>
              </div>
            );
          }
          return (
            <div className="flex items-center h-full">
              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                Not featured
              </span>
            </div>
          );
        },
        sortable: false,
        filter: false,
      },
      {
        headerName: 'Updated',
        width: 120,
        cellRenderer: (params) => {
          const date = params.data?.displayCreatedDate ?? params.data?.createdDate;
          return (
            <div className="flex items-center h-full gap-1 text-sm text-gray-500">
              <Calendar size={14} className="flex-shrink-0" />
              <span>{formatDate(date)}</span>
            </div>
          );
        },
        filter: false,
      },
      {
        headerName: 'Actions',
        width: 112,
        minWidth: 100,
        pinned: 'right',
        cellRenderer: (params) => {
          const row = params.data;
          const designId = row?.designSubmissionId ?? row?.id;
          const isDraft = (row?.rawSubmissionStatus ?? getStatusDisplay(row?.status)) === 'DRAFT';
          // BOQ is meaningful only for finalized iterations — materials are
          // priced against the accepted design, not exploratory samples.
          const isFinalIter = Boolean(row?.rowIterationIsFinal);
          return (
            <div className="flex items-center gap-1 h-full">
              <ViewButton onClick={() => handleViewClick(row)} disabled={isDeleting} />
              {isDraft && (
                <>
                  <EditButton
                    onClick={() => handleEditClick(row)}
                    disabled={isDeleting}
                    title="Edit design submission"
                  />
                  <DeleteButton
                    onClick={() => handleDeleteClick(designId)}
                    disabled={isDeleting}
                    title="Delete submission"
                  />
                </>
              )}
              {isFinalIter && (
                <SmallIconButton
                  icon={FileText}
                  onClick={() => handleEditBoqClick(row)}
                  disabled={isDeleting}
                  title="Edit BOQ"
                />
              )}
            </div>
          );
        },
        sortable: false,
        filter: false,
      },
    ],
    [
      getStatusColor,
      formatDate,
      handleViewClick,
      handleEditClick,
      handleEditBoqClick,
      handleDeleteClick,
      isDeleting,
    ]
  );

  const defaultColDef = useMemo(
    () => ({
      sortable: true,
      filter: false,
      resizable: true,
      floatingFilter: false,
      cellStyle: { display: 'flex', alignItems: 'center' },
    }),
    []
  );
  // eslint-disable-next-line no-unused-vars
  const _legacyDefaultColDef = defaultColDef;
  // eslint-disable-next-line no-unused-vars
  const _legacyColumnDefs = columnDefs;

  // DataTable columns — translates the AG-Grid columnDefs above into the
  // canonical primitive's format. Same fields, fixed widths so nothing
  // overlaps, truncation + tooltips for long values.
  const dtColumns = useMemo(
    () => [
      {
        key: 'design',
        header: 'Design',
        width: 140,
        render: (row) => (
          <div className="min-w-0">
            <p className="text-sm font-semibold text-slate-900 truncate" title={row.gridTitle}>
              {row.gridTitle || '—'}
            </p>
            {row.gridSubtitle && (
              <p className="text-xs text-slate-500 truncate" title={row.gridSubtitle}>
                {row.gridSubtitle}
              </p>
            )}
          </div>
        ),
      },
      {
        key: 'unit',
        header: 'Unit',
        width: 70,
        render: (r) => (
          <span className="text-sm font-semibold text-slate-900 tabular-nums">
            {r?.idLead?.property?.propertyNo ?? '—'}
          </span>
        ),
      },
      {
        key: 'config',
        header: 'Config',
        width: 110,
        render: (r) => (
          <span className="text-sm text-slate-700 truncate block" title={r?.idLead?.propertyConfig?.configName}>
            {r?.idLead?.propertyConfig?.configName ?? '—'}
          </span>
        ),
      },
      {
        key: 'project',
        header: 'Project',
        width: 140,
        render: (r) => (
          <span className="text-sm text-slate-700 truncate block"
            title={r?.idLead?.property?.projectPhase?.project?.projectName}>
            {r?.idLead?.property?.projectPhase?.project?.projectName ?? '—'}
          </span>
        ),
      },
      {
        key: 'type',
        header: 'Type',
        width: 80,
        render: (r) => {
          const hasIter = r?.designIterationId != null;
          const isFinal = r?.rowIterationIsFinal;
          const label = !hasIter ? '—' : isFinal ? 'Final' : 'Sample';
          const cls = !hasIter
            ? 'bg-slate-100 text-slate-500 border-slate-200'
            : isFinal
            ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
            : 'bg-slate-50 text-slate-700 border-slate-200';
          return (
            <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-[11px] font-medium border ${cls}`}>
              {label}
            </span>
          );
        },
      },
      {
        key: 'status',
        header: 'Status',
        width: 130,
        render: (r) => {
          let statusStr = pickIterationStatus({
            iterationDeveloperStatus: r?.iterationDeveloperStatus,
            iterationWorkflowStatus: r?.iterationWorkflowStatus,
            rowIterationIsFinal: r?.rowIterationIsFinal,
          });
          if (!statusStr) {
            statusStr =
              getLatestStatusKey(r?.idLead?.status) ??
              r?.submissionStatus ??
              getStatusDisplay(r?.status);
          }
          const displayStatus = formatStatusLabel(statusStr);
          const cls = statusStr && statusStr !== '-' ? getStatusColor(statusStr) : 'bg-slate-100 text-slate-500';
          return (
            <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-[11px] font-medium ${cls}`}>
              {displayStatus}
            </span>
          );
        },
      },
      {
        key: 'deadline',
        header: 'Deadline',
        width: 130,
        render: (r) => {
          const iterAckDeadline = r?.iterIdAcknowledgementDeadline;
          const iterAcked = Boolean(r?.iterIsIdAcknowledged);
          const iterStatus = (r?.iterationWorkflowStatus ?? '').toString().toUpperCase();
          const iterRejected = iterStatus === 'REJECTED';
          const iterDecided = iterStatus === 'APPROVED' || iterRejected;
          const iterAckExpired = hasStatus(r?.idLead, ID_ITER_ACK_EXPIRED);
          // Hide the ack countdown once the buyer has decided — the
          // designer no longer needs to ack feedback on a settled iteration.
          const showAck = Boolean(iterAckDeadline) && !iterAcked && !iterDecided;
          if (showAck) {
            return (
              <CountdownBadge
                deadline={iterAckDeadline}
                label="Ack by"
                expiredLabel="Ack expired"
              />
            );
          }
          if (iterRejected && iterAckExpired) {
            return (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-rose-100 text-rose-700 border border-rose-300">
                <AlertTriangle size={12} className="shrink-0" aria-hidden />
                Auto-rejected
              </span>
            );
          }
          return <span className="text-xs text-slate-400">—</span>;
        },
      },
      {
        key: 'price',
        header: 'Price',
        width: 110,
        render: (r) => {
          const price = r?.estimatedPrice ?? r?.idLead?.interiorDesignerPortfolioItems?.[0]?.overallCost;
          const val = price != null ? parseFloat(price) : null;
          return (
            <span className="text-sm font-semibold text-slate-900 tabular-nums">
              {val != null ? `₹${Number(val).toLocaleString('en-IN')}` : '—'}
            </span>
          );
        },
      },
      {
        key: 'featured',
        header: 'Feature',
        width: 110,
        render: (r) => {
          const hasIter = r?.designIterationId != null;
          const hasPhase = r?.projectPhaseId != null && r.projectPhaseId !== '';
          const featured = r?.isFeaturedForBrowse;
          if (!hasIter) return <span className="text-xs text-slate-400">—</span>;
          if (!hasPhase) return <span className="text-xs text-amber-700">No phase</span>;
          if (featured) {
            return (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] font-medium bg-amber-50 text-amber-800 border border-amber-200">
                <Star size={11} className="fill-amber-500 text-amber-600" />
                Featured
              </span>
            );
          }
          return (
            <span className="inline-flex items-center px-2 py-0.5 rounded-md text-[11px] font-medium bg-slate-100 text-slate-600">
              Not featured
            </span>
          );
        },
      },
      {
        key: 'updated',
        header: 'Updated',
        width: 100,
        render: (r) => (
          <div className="flex items-center gap-1 text-xs text-slate-600 tabular-nums">
            <Calendar size={11} className="text-slate-400" />
            <span>{formatDate(r?.displayCreatedDate ?? r?.createdDate)}</span>
          </div>
        ),
      },
      {
        key: 'actions',
        header: 'Actions',
        width: 120,
        align: 'center',
        render: (row) => {
          const designId = row?.designSubmissionId ?? row?.id;
          const isDraft = (row?.rawSubmissionStatus ?? getStatusDisplay(row?.status)) === 'DRAFT';
          const isFinalIter = Boolean(row?.rowIterationIsFinal);
          return (
            <div className="flex items-center justify-center gap-1">
              <ViewButton onClick={() => handleViewClick(row)} disabled={isDeleting} />
              {isDraft && (
                <>
                  <EditButton onClick={() => handleEditClick(row)} disabled={isDeleting} title="Edit" />
                  <DeleteButton onClick={() => handleDeleteClick(designId)} disabled={isDeleting} title="Delete" />
                </>
              )}
              {isFinalIter && (
                <SmallIconButton icon={FileText} onClick={() => handleEditBoqClick(row)} disabled={isDeleting} title="Edit BOQ" />
              )}
            </div>
          );
        },
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [getStatusColor, formatDate, handleViewClick, handleEditClick, handleEditBoqClick, handleDeleteClick, isDeleting]
  );

  // Tab metadata — drives both the pill nav and the KPI strip below it.
  const TAB_META = [
    {
      id: 'iterations',
      label: 'Iterations',
      icon: Palette,
      description: 'All design iterations across your submissions',
      count: filteredRows.length,
      countLabel: 'Total iterations',
    },
    {
      id: 'featured',
      label: 'Featured',
      icon: Star,
      description: 'Design iterations currently featured for buyer browse',
      count: totalFeaturedCount,
      countLabel: 'Featured for browse',
    },
    {
      id: 'portfolio',
      label: 'Portfolio',
      icon: FolderOpen,
      description: 'Showcase your past work — maximum 5 items',
      count: portfolioItems.length,
      countLabel: 'Portfolio items',
    },
  ];
  const activeMeta = TAB_META.find((t) => t.id === mainTab) || TAB_META[0];

  return (
    <div className="p-4 sm:p-6 lg:p-8 flex flex-col gap-6 min-h-[calc(100vh-4rem)] bg-slate-50/40">
      {/* Hero band — same family as Dashboard / Reports / Approvals. */}
      <div className="rounded-2xl shadow-sm bg-gradient-to-r from-slate-800 via-slate-700 to-slate-600 p-5 lg:p-6 text-white">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <p className="text-xs uppercase tracking-wider text-slate-300 flex items-center gap-1.5">
              <Palette size={12} />
              Studio
            </p>
            <h1 className="text-xl sm:text-2xl font-semibold tracking-tight mt-1">My designs</h1>
            <p className="text-xs text-slate-300 mt-1">{activeMeta.description}</p>
          </div>
        </div>
      </div>

      {/* KPI tiles — one per tab, scannable at a glance. */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {TAB_META.map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => selectMainTab(t.id)}
            className={`text-left bg-white rounded-2xl border shadow-sm p-5 flex items-center gap-3 transition-all hover:shadow-md ${
              mainTab === t.id
                ? 'border-flamingo-300 ring-1 ring-flamingo-200'
                : 'border-slate-200 hover:border-flamingo-200'
            }`}
          >
            <div
              className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${
                mainTab === t.id
                  ? 'bg-flamingo-500 text-white'
                  : 'bg-slate-100 text-slate-700'
              }`}
            >
              <t.icon size={20} />
            </div>
            <div>
              <p className="text-xs uppercase tracking-wide text-slate-500 font-medium">
                {t.countLabel}
              </p>
              <p className="text-2xl font-bold text-slate-900 tabular-nums">{t.count}</p>
            </div>
          </button>
        ))}
      </div>

      {/* Pill tab nav — matches dashboard / reports / approvals / sales. */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-1.5 inline-flex gap-1 self-start flex-wrap">
        {TAB_META.map(({ id, label, icon: Icon, count }) => {
          const active = mainTab === id;
          return (
            <button
              key={id}
              type="button"
              onClick={() => selectMainTab(id)}
              className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-medium transition-colors ${
                active
                  ? 'bg-gradient-to-r from-flamingo-500 to-flamingo-600 text-white shadow-sm'
                  : 'text-slate-600 hover:text-slate-900 hover:bg-slate-50'
              }`}
            >
              <Icon size={14} className={active && id === 'featured' ? 'fill-white' : ''} />
              {label}
              {count > 0 && (
                <span
                  className={`tabular-nums px-1.5 py-0.5 rounded-md text-[10px] font-semibold ${
                    active ? 'bg-white/20 text-white' : 'bg-slate-100 text-slate-600'
                  }`}
                >
                  {count}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Iterations filter strip — search + final/samples toggle, wrapped
          in the canonical card so it lines up with the table below. */}
      {mainTab === 'iterations' && (
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4">
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="flex-1 relative">
              <Search
                size={14}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none"
              />
              <input
                type="text"
                placeholder="Search iterations, titles, notes…"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-9 pr-3 py-2 text-sm rounded-lg border border-slate-300 bg-white focus:border-flamingo-500 focus:ring-1 focus:ring-flamingo-200 outline-none"
              />
            </div>
            <div className="relative sm:w-52">
              <Filter
                size={14}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none"
              />
              <select
                value={finalFilter}
                onChange={(e) => setFinalFilter(e.target.value)}
                className="w-full pl-9 pr-3 py-2 text-sm rounded-lg border border-slate-300 bg-white focus:border-flamingo-500 focus:ring-1 focus:ring-flamingo-200 outline-none appearance-none"
                aria-label="Filter by final or sample designs"
              >
                <option value="ALL">All iterations</option>
                <option value="FINAL">Final only</option>
                <option value="SAMPLES">Samples only</option>
              </select>
            </div>
          </div>
        </div>
      )}

      {mainTab === 'iterations' && (
        <DataTable
          columns={dtColumns}
          data={filteredRows}
          loading={loading}
          minWidth={1240}
          rowKey={(r, i) => r?._rowKey ?? r?.designSubmissionId ?? i}
          emptyState={
            <div className="px-6 py-12 text-center">
              <Palette size={32} className="mx-auto text-slate-300" />
              <p className="mt-3 text-sm font-semibold text-slate-700">No iterations found</p>
              <p className="text-xs text-slate-500 mt-1">
                {searchQuery || finalFilter !== 'ALL'
                  ? 'Try adjusting your search or filter.'
                  : 'Get started by creating your first design submission.'}
              </p>
            </div>
          }
        />
      )}

      {mainTab === 'featured' && (
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden min-h-[400px]">
          {loading ? (
            <div className="text-center py-12 text-slate-500">Loading…</div>
          ) : phasesMeta.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 px-6 text-center text-slate-500">
              <Star size={36} className="mb-3 text-slate-300" />
              <p className="text-base font-semibold text-slate-900 mb-1">No phases yet</p>
              <p className="text-sm max-w-md">
                You'll see your featured slots here once you have leads in a subscribed project phase.
              </p>
            </div>
          ) : (
            <DragDropContext onDragEnd={handleFeaturedDragEnd}>
              {/* Phase pill-bar — shows project · phase name with a count
                  badge per pill (n/8) so the cap is visible at a glance. */}
              <div className="px-4 sm:px-6 pt-4 sm:pt-5 pb-3 border-b border-slate-100">
                <div className="flex flex-wrap items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setFeaturedPhaseFilter(FEATURED_PHASE_FILTER_ALL)}
                    className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold border transition-colors ${
                      featuredPhaseFilter === FEATURED_PHASE_FILTER_ALL
                        ? 'bg-flamingo-600 border-flamingo-600 text-white shadow-sm'
                        : 'bg-white border-slate-200 text-slate-700 hover:border-slate-300 hover:bg-slate-50'
                    }`}
                  >
                    All phases
                    <span
                      className={`inline-flex items-center justify-center min-w-[20px] h-[20px] px-1.5 rounded-full text-[10px] font-bold ${
                        featuredPhaseFilter === FEATURED_PHASE_FILTER_ALL
                          ? 'bg-white/20 text-white'
                          : 'bg-slate-100 text-slate-700'
                      }`}
                    >
                      {totalFeaturedCount}
                    </span>
                  </button>
                  {phasesMeta.map(({ phaseKey, label, featuredCount }) => {
                    const active = featuredPhaseFilter === phaseKey;
                    const isFull = featuredCount >= MAX_FEATURED_PER_PHASE;
                    return (
                      <button
                        key={phaseKey}
                        type="button"
                        onClick={() => setFeaturedPhaseFilter(phaseKey)}
                        title={label}
                        className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold border transition-colors max-w-full ${
                          active
                            ? 'bg-flamingo-600 border-flamingo-600 text-white shadow-sm'
                            : 'bg-white border-slate-200 text-slate-700 hover:border-slate-300 hover:bg-slate-50'
                        }`}
                      >
                        <span className="truncate max-w-[220px]">{label}</span>
                        <span
                          className={`inline-flex items-center justify-center min-w-[34px] h-[20px] px-1.5 rounded-full text-[10px] font-bold tabular-nums ${
                            active
                              ? 'bg-white/20 text-white'
                              : isFull
                                ? 'bg-amber-100 text-amber-800'
                                : 'bg-slate-100 text-slate-700'
                          }`}
                        >
                          {featuredCount}/{MAX_FEATURED_PER_PHASE}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>
              <div className="p-4 sm:p-6 space-y-8 max-h-[calc(100vh-260px)] overflow-y-auto">
                {phasesMeta
                  .filter(({ phaseKey }) =>
                    featuredPhaseFilter === FEATURED_PHASE_FILTER_ALL
                      ? true
                      : String(featuredPhaseFilter) === String(phaseKey)
                  )
                  .map(({ phaseKey, label, featuredCount }) => {
                    const items = Array.isArray(featuredByPhase[phaseKey])
                      ? featuredByPhase[phaseKey]
                      : [];
                    const emptySlots = Math.max(0, MAX_FEATURED_PER_PHASE - featuredCount);
                    // When viewing All, hide phases that have nothing featured
                    // (the pill still shows them so the designer can drill in).
                    if (
                      featuredPhaseFilter === FEATURED_PHASE_FILTER_ALL &&
                      featuredCount === 0
                    ) {
                      return null;
                    }
                    const isFull = featuredCount >= MAX_FEATURED_PER_PHASE;
                    const phaseDirty = Boolean(dirtyOrderPhases[phaseKey]);
                    const savingThis = savingOrderPhaseKey === phaseKey;
                    const droppableId = `${FEATURED_DROPPABLE_PREFIX}${phaseKey}`;
                    return (
                      <section key={phaseKey} className="rounded-md border border-gray-200 overflow-hidden">
                        <div className="bg-gray-50 px-4 py-3 flex flex-wrap items-center justify-between gap-3 border-b border-gray-200">
                          <div className="flex items-center gap-2 min-w-0">
                            <h2 className="text-sm font-semibold text-gray-900 truncate">
                              {label}
                            </h2>
                            <span
                              className={`inline-flex items-center justify-center shrink-0 min-w-[40px] h-[20px] px-1.5 rounded-full text-[10px] font-bold tabular-nums ${
                                isFull
                                  ? 'bg-amber-100 text-amber-800 border border-amber-200'
                                  : 'bg-slate-100 text-slate-700'
                              }`}
                              title={
                                isFull
                                  ? `${featuredCount} of ${MAX_FEATURED_PER_PHASE} slots filled (full)`
                                  : `${featuredCount} of ${MAX_FEATURED_PER_PHASE} slots filled`
                              }
                            >
                              {featuredCount}/{MAX_FEATURED_PER_PHASE}
                            </span>
                          </div>
                          {phaseDirty ? (
                            <button
                              type="button"
                              disabled={savingThis}
                              onClick={() => saveFeaturedOrder(phaseKey)}
                              className="text-sm font-semibold text-white bg-flamingo-600 hover:bg-flamingo-700 disabled:opacity-50 px-4 py-2 rounded-lg"
                            >
                              {savingThis ? 'Saving…' : 'Save order'}
                            </button>
                          ) : (
                            <span className="text-xs text-gray-500">
                              Drag the handle to reorder · top shows first in browse
                            </span>
                          )}
                        </div>
                        <Droppable droppableId={droppableId}>
                          {(dropProvided) => (
                            <ul
                              ref={dropProvided.innerRef}
                              {...dropProvided.droppableProps}
                              className="divide-y divide-gray-100"
                            >
                              {items.map((apiItem, index) => {
                                const iterId = extractFeaturedIterationId(apiItem);
                                const featuredBrowseId = extractFeaturedDesignIterationId(apiItem);
                                const stableKey =
                                  featuredBrowseId != null
                                    ? `f-${featuredBrowseId}`
                                    : `i-${iterId ?? 'n'}-${index}`;
                                const draggableId = `${phaseKey}__${stableKey}`;
                                const merged = buildFeaturedViewRow(apiItem, iterationRows);
                                const propNo = merged?.idLead?.property?.propertyNo ?? '—';
                                const configName = merged?.idLead?.propertyConfig?.configName ?? '—';
                                return (
                                  <Draggable
                                    key={draggableId}
                                    draggableId={draggableId}
                                    index={index}
                                    isDragDisabled={savingThis}
                                  >
                                    {(dragProvided, snapshot) => (
                                      <li
                                        ref={dragProvided.innerRef}
                                        {...dragProvided.draggableProps}
                                        className={`flex flex-wrap items-center gap-2 pl-1 pr-4 py-3 bg-white ${
                                          snapshot.isDragging
                                            ? 'shadow-lg ring-2 ring-flamingo-200 bg-flamingo-50/90'
                                            : 'hover:bg-gray-50/80'
                                        }`}
                                      >
                                        <div
                                          {...dragProvided.dragHandleProps}
                                          className="flex items-center justify-center shrink-0 w-9 py-1 text-gray-400 hover:text-gray-700 cursor-grab active:cursor-grabbing touch-manipulation rounded-md hover:bg-gray-100"
                                          title="Drag to reorder"
                                        >
                                          <GripVertical size={20} aria-hidden />
                                        </div>
                                        <span className="w-7 shrink-0 text-center text-sm font-semibold text-gray-400">
                                          {index + 1}
                                        </span>
                                        <div className="flex-1 min-w-0">
                                          <div className="font-medium text-gray-900 truncate">
                                            {merged.gridTitle ?? merged.title ?? 'Design'}
                                          </div>
                                          <div className="text-xs text-gray-500 truncate">
                                            {merged.gridSubtitle ?? ''}
                                            {propNo !== '—' || configName !== '—' ? (
                                              <span className="text-gray-400">
                                                {' '}
                                                · {propNo}
                                                {configName !== '—' ? ` · ${configName}` : ''}
                                              </span>
                                            ) : null}
                                          </div>
                                        </div>
                                        <div className="flex items-center shrink-0">
                                          <OutlineButton
                                            type="button"
                                            onClick={() =>
                                              setRemoveFeaturedTarget({
                                                apiItem,
                                                phaseKey,
                                                title: merged.gridTitle ?? merged.title ?? 'Design',
                                              })
                                            }
                                            disabled={savingThis || isRemovingFeatured}
                                            className="!w-auto" 
                                          >
                                            Remove from featured
                                          </OutlineButton>
                                        </div>
                                      </li>
                                    )}
                                  </Draggable>
                                );
                              })}
                              {dropProvided.placeholder}
                            </ul>
                          )}
                        </Droppable>
                        {/* Empty slot placeholders — make the n/8 cap tactile.
                            Clicking opens a picker scoped to this phase. */}
                        {emptySlots > 0 && (
                          <ul className="divide-y divide-dashed divide-slate-200 border-t border-dashed border-slate-200">
                            {Array.from({ length: emptySlots }).map((_, i) => {
                              const slotIndex = featuredCount + i + 1;
                              return (
                                <li
                                  key={`empty-${phaseKey}-${i}`}
                                  className="flex items-center gap-2 pl-1 pr-4 py-3"
                                >
                                  <span className="w-9 shrink-0" aria-hidden />
                                  <span className="w-7 shrink-0 text-center text-sm font-semibold text-slate-300">
                                    {slotIndex}
                                  </span>
                                  <button
                                    type="button"
                                    onClick={() => setFeaturedPickerPhaseKey(phaseKey)}
                                    className="flex-1 min-w-0 text-left rounded-md border border-dashed border-slate-300 hover:border-flamingo-400 hover:bg-flamingo-50/40 px-3 py-2 text-sm text-slate-500 hover:text-flamingo-700 transition-colors inline-flex items-center gap-2"
                                  >
                                    <Plus size={14} aria-hidden className="shrink-0" />
                                    <span className="truncate">Feature a design in this slot</span>
                                  </button>
                                </li>
                              );
                            })}
                          </ul>
                        )}
                      </section>
                    );
                  })}
              </div>
            </DragDropContext>
          )}
        </div>
      )}

      {mainTab === 'portfolio' && (
        <div className="space-y-4">
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm px-4 py-3 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
            <p className="text-sm text-slate-600 min-w-0 sm:pr-4">
              Add items through the full wizard (metadata and BOQ). Use view or edit from each card.
            </p>
            {portfolioItems.length < 5 && (
              <PrimaryButton
                type="button"
                onClick={() => navigate('/interior-designer/add-portfolio')}
                className="!w-auto shrink-0 inline-flex items-center justify-center gap-2 whitespace-nowrap !px-3 !py-1.5 text-sm"
              >
                <Plus size={14} aria-hidden />
                Add portfolio item
              </PrimaryButton>
            )}
          </div>
          <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6 sm:p-8">
            {loadingPortfolio ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {[...Array(3)].map((_, index) => (
                  <div
                    key={index}
                    className="border border-gray-200 rounded-lg overflow-hidden animate-pulse"
                  >
                    <div className="aspect-video bg-gray-200" />
                    <div className="p-4 space-y-3">
                      <div className="h-5 bg-gray-200 rounded w-3/4" />
                      <div className="space-y-2">
                        <div className="h-3 bg-gray-200 rounded w-full" />
                        <div className="h-3 bg-gray-200 rounded w-5/6" />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : portfolioItems.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {sortedPortfolioItems.map((item, index) => {
                  const getImageSrc = (image) => {
                    if (!image) return null;
                    const s3Key = image?.s3_key || image?.s3Key;
                    if (s3Key) return portfolioImageUrls[s3Key] || null;
                    return null;
                  };
                  const firstImage = item.images && item.images.length > 0 ? item.images[0] : null;
                  const imageSrc = getImageSrc(firstImage);
                  const portfolioId =
                    item.id || item.interiorDesignerPortfolioItemId || item.portfolioId;
                  const shortlistCount = getPortfolioPopularityCount(item);
                  const showTopBadge = shortlistCount > 0 && index < 3;
                  const rankLabel = index === 0 ? 'Top liked' : index === 1 ? '2nd most liked' : '3rd most liked';

                  return (
                    <div
                      key={item.id || index}
                      className="border border-gray-200 rounded-lg overflow-hidden hover:shadow-md transition-shadow cursor-pointer relative group"
                      onClick={() => setSelectedPortfolio(item)}
                    >
                      {imageSrc ? (
                        <div className="aspect-video bg-gray-100 relative">
                          <LoadingImage
                            src={imageSrc}
                            alt={item.title}
                            containerClassName="w-full h-full"
                            className="w-full h-full object-cover"
                            fallback={
                              <div className="w-full h-full flex items-center justify-center bg-gray-100">
                                <ImageIcon className="text-gray-300" size={32} />
                              </div>
                            }
                          />
                          {Array.isArray(item.images) && item.images.length > 1 && (
                            <div className="absolute top-2 right-2 bg-black bg-opacity-50 text-white text-xs px-2 py-1 rounded">
                              +{item.images.length - 1}
                            </div>
                          )}
                          {showTopBadge && (
                            <div className="absolute bottom-2 right-2 inline-flex items-center gap-1 rounded-full bg-amber-100 text-amber-900 border border-amber-200 px-2.5 py-1 text-[11px] font-semibold">
                              <Star size={12} className="fill-amber-500 text-amber-600" />
                              {rankLabel}
                            </div>
                          )}
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              if (portfolioId) {
                                setPortfolioDeleteTarget({ id: portfolioId, title: item.title });
                              } else {
                                toast.error('Portfolio ID not found');
                              }
                            }}
                            className="absolute top-2 left-2 bg-red-500 hover:bg-red-600 text-white p-1.5 rounded-full opacity-0 group-hover:opacity-100 transition-opacity shadow-lg"
                            title="Delete portfolio"
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                      ) : (
                        <div className="aspect-video bg-gray-100 flex items-center justify-center relative">
                          <ImageIcon className="text-gray-300" size={32} />
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              if (portfolioId) {
                                setPortfolioDeleteTarget({ id: portfolioId, title: item.title });
                              } else {
                                toast.error('Portfolio ID not found');
                              }
                            }}
                            className="absolute top-2 left-2 bg-red-500 hover:bg-red-600 text-white p-1.5 rounded-full opacity-0 group-hover:opacity-100 transition-opacity shadow-lg"
                            title="Delete portfolio"
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                      )}
                      <div className="p-4">
                        <h4 className="font-semibold text-gray-900 mb-1 hover:text-flamingo-600 transition-colors">
                          {item.title}
                        </h4>
                        {item.description && (
                          <p className="text-sm text-gray-600 mb-2 line-clamp-2">{item.description}</p>
                        )}
                        <div className="flex items-center gap-4 text-xs text-gray-500 flex-wrap">
                          {item.propertyType && <span>{item.propertyType}</span>}
                          {item.completedDate && (
                            <span>{new Date(item.completedDate).toLocaleDateString()}</span>
                          )}
                          <span className="inline-flex items-center gap-1 font-medium text-gray-700">
                            <Star size={12} className="text-amber-500" />
                            {shortlistCount} interactions
                          </span>
                        </div>
                        <div className="mt-3 flex flex-col sm:flex-row gap-2">
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedPortfolio(item);
                            }}
                            className="flex-1 px-3 py-2 border border-gray-300 text-gray-700 rounded hover:bg-gray-50 transition-colors flex items-center justify-center gap-2 text-sm font-medium"
                          >
                            <Eye className="w-4 h-4" />
                            View details
                          </button>
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              if (portfolioId) {
                                navigate(`/interior-designer/edit-portfolio/${portfolioId}`);
                              } else {
                                toast.error('Portfolio ID not found');
                              }
                            }}
                            className="flex-1 px-3 py-2 border border-flamingo-200 text-flamingo-700 bg-flamingo-50/80 rounded hover:bg-flamingo-100 transition-colors flex items-center justify-center gap-2 text-sm font-medium"
                          >
                            <Pencil className="w-4 h-4" />
                            Edit
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-12 text-center">
                <ImageIcon size={48} className="mx-auto text-gray-300 mb-3" />
                <p className="text-gray-500 text-sm mb-2">No portfolio items yet</p>
                <button
                  type="button"
                  onClick={() => navigate('/interior-designer/add-portfolio')}
                  className="text-flamingo-500 hover:text-flamingo-600 text-sm font-medium"
                >
                  Add your first portfolio item
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      <PortfolioViewModal
        isOpen={selectedPortfolio !== null}
        onClose={() => setSelectedPortfolio(null)}
        portfolio={selectedPortfolio}
        designer={
          userInfo
            ? {
                interiorDesignerId: userInfo.loginId,
                name: userInfo.name,
                email: userInfo.email,
                mobile: userInfo.phone,
                yearsExperience: userInfo.experience,
                specialization: userInfo.specialization,
                companyName: userInfo.companyName,
              }
            : null
        }
      />

      <ConfirmationModal
        isOpen={portfolioDeleteTarget !== null}
        title="Delete portfolio item?"
        onConfirm={handlePortfolioDeleteConfirm}
        onCancel={() => !isDeletingPortfolio && setPortfolioDeleteTarget(null)}
        cancelButtonText="Cancel"
        confirmButtonText="Delete"
        loadingText="Deleting..."
        isLoading={isDeletingPortfolio}
        panelClassName="max-w-md"
      >
        <p>
          Are you sure you want to delete{' '}
          <span className="font-semibold text-gray-900">{portfolioDeleteTarget?.title}</span>? This cannot be
          undone.
        </p>
      </ConfirmationModal>

      <ConfirmationModal
        isOpen={removeFeaturedTarget !== null}
        title="Remove from featured browse?"
        onConfirm={handleRemoveFeaturedConfirm}
        onCancel={() => !isRemovingFeatured && setRemoveFeaturedTarget(null)}
        cancelButtonText="Cancel"
        confirmButtonText="Remove"
        loadingText="Removing…"
        isLoading={isRemovingFeatured}
        panelClassName="max-w-md"
      >
        <p>
          Remove{' '}
          <span className="font-semibold text-gray-900">{removeFeaturedTarget?.title}</span> from featured designs?
          Buyers will no longer see it in browse for this project phase until you feature it again.
        </p>
      </ConfirmationModal>

      {editingDesign && (
        <DesignSubmissionModal
          isOpen={showDesignModal}
          portfolioId={editingDesign.portfolioId ?? editingDesign.interiorDesignerPortfolioItemId ?? editingDesign.portfolio_id}
          onClose={handleModalClose}
          configId={editingDesign.propertyConfigId ?? editingDesign.configId}
          leadId={editingDesign.idLead?.interiorDesignerLeadId}
          lead={editingDesign.idLead ?? null}
          onSuccess={fetchDesigns}
          editingDesign={editingDesign}
          editingIterationId={editingDesign.designIterationId ?? null}
          startAtStep={designModalStep}
        />
      )}

      <DesignViewModal
        isOpen={viewingDesign !== null}
        onClose={() => setViewingDesign(null)}
        design={viewingDesign}
      />

      <ConfirmationModal
        isOpen={showDeleteConfirm}
        onConfirm={handleDeleteConfirm}
        onCancel={() => {
          setShowDeleteConfirm(false);
          setDeletingDesignId(null);
        }}
        message="Are you sure you want to delete this design? This action cannot be undone."
        isLoading={isDeleting}
        loadingText="Deleting..."
      />

      {/* Paused: feature-from-grid modal — re-enable with FeaturedBrowseConfirmDetails + state/handlers above.
      {featuredConfirmRow && (
        <ConfirmationModal
          isOpen
          title="Feature for browse?"
          onConfirm={handleFeaturedConfirmSubmit}
          onCancel={handleFeaturedConfirmClose}
          cancelButtonText="Cancel"
          confirmButtonText="Make featured"
          loadingText="Featuring…"
          isLoading={Boolean(featuringRowKey)}
          panelClassName="max-w-lg"
        >
          <FeaturedBrowseConfirmDetails row={featuredConfirmRow} />
        </ConfirmationModal>
      )}
      */}

      {/* Slot picker — opens from an empty featured slot. Lists the
          designer's iterations in this phase that aren't already featured;
          click one to feature it. */}
      {featuredPickerPhaseKey && (() => {
        const phaseKey = featuredPickerPhaseKey;
        const candidates = getCandidateIterationsForPhase(phaseKey);
        const phaseMeta = phasesMeta.find((p) => p.phaseKey === phaseKey);
        return (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4"
            role="dialog"
            aria-modal="true"
            onClick={() => setFeaturedPickerPhaseKey(null)}
          >
            <div
              className="bg-white rounded-2xl shadow-xl max-w-2xl w-full max-h-[80vh] flex flex-col overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-start justify-between gap-3 px-5 py-4 border-b border-slate-100">
                <div className="min-w-0">
                  <h3 className="text-base font-semibold text-slate-900 truncate">
                    Feature a design in {phaseMeta?.label ?? `Phase ${phaseKey}`}
                  </h3>
                  <p className="text-xs text-slate-500 mt-0.5">
                    Pick from your iterations for this phase. They'll appear when buyers browse.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setFeaturedPickerPhaseKey(null)}
                  className="shrink-0 rounded-md p-1 text-slate-500 hover:text-slate-700 hover:bg-slate-100"
                  aria-label="Close"
                >
                  <X size={18} />
                </button>
              </div>
              <div className="flex-1 overflow-y-auto p-3 sm:p-4">
                {candidates.length === 0 ? (
                  <div className="text-center py-12 px-6">
                    <Star size={28} className="mx-auto mb-3 text-slate-300" />
                    <p className="text-sm font-semibold text-slate-700 mb-1">
                      No eligible iterations
                    </p>
                    <p className="text-xs text-slate-500 max-w-sm mx-auto">
                      Every iteration you own in this phase is already featured, or you have no iterations here yet.
                    </p>
                  </div>
                ) : (
                  <ul className="space-y-2">
                    {candidates.map((cand) => {
                      const iterId = Number(cand.designIterationId ?? cand.id);
                      const busy = featuringIterationId === iterId;
                      const isFinal = Boolean(cand.rowIterationIsFinal);
                      return (
                        <li
                          key={iterId}
                          className="flex items-center gap-3 p-3 rounded-lg border border-slate-200 hover:border-flamingo-300 hover:bg-flamingo-50/40 transition-colors"
                        >
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <p className="text-sm font-semibold text-slate-900 truncate">
                                {cand.gridTitle ?? 'Design'}
                              </p>
                              {isFinal && (
                                <span className="inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200 shrink-0">
                                  Final
                                </span>
                              )}
                            </div>
                            <p className="text-xs text-slate-500 truncate">
                              {cand.gridSubtitle ?? `Iteration ${cand.iterationNumber ?? '?'}`}
                            </p>
                          </div>
                          <PrimaryButton
                            type="button"
                            disabled={busy || featuringIterationId != null}
                            onClick={() => handleFeatureIterationInPhase(phaseKey, iterId)}
                            className="!w-auto shrink-0 text-sm !px-3 !py-1.5"
                          >
                            {busy ? 'Featuring…' : 'Feature'}
                          </PrimaryButton>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
};

export default Designs;
